"use client";

import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { ArrowUpDown, Copy, Check } from "lucide-react";
import { useState } from "react";
import { Employee } from "./employeeTableColumns";

// Email cell component with copy functionality
const EmailCell = ({ email }: { email: string }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group flex items-center gap-2 text-left">
      <span>{email}</span>
      <button
        onClick={copyToClipboard}
        className="opacity-50 group-hover:opacity-100 transition-opacity hover:opacity-100"
        title="Copy email"
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-600" />
        ) : (
          <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
        )}
      </button>
    </div>
  );
};

// Helper function for employee type badge colors
const getEmployeeTypeBadgeColor = (type?: string) => {
  switch (type) {
    case "Intern":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "Part-Time":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "Employee":
    default:
      return "bg-green-100 text-green-800 border-green-200";
  }
};

// Read-only column definitions for employee view
export const createEmployeeColumnsReadOnly = (): ColumnDef<Employee>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      return <div className="font-medium text-left">{name}</div>;
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Email
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const email = row.getValue("email") as string;
      return <EmailCell email={email} />;
    },
  },
  {
    accessorKey: "phone",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Phone
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const phone = row.getValue("phone") as string;
      return <div className="text-left">{phone || "N/A"}</div>;
    },
  },
  {
    accessorKey: "employeeType",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Type
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const type = (row.getValue("employeeType") as string) || "Employee";
      return (
        <div className="text-left">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${getEmployeeTypeBadgeColor(
              type
            )}`}
          >
            {type}
          </span>
        </div>
      );
    },
  },
  // {
  //   accessorKey: "company",
  //   header: ({ column }) => {
  //     return (
  //       <Button
  //         variant="ghost"
  //         onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  //         className="h-auto p-0 justify-start"
  //       >
  //         Company
  //         <ArrowUpDown className="ml-2 h-4 w-4" />
  //       </Button>
  //     );
  //   },
  //   cell: ({ row }) => {
  //     const company = row.getValue("company") as string;
  //     return (
  //       <div className="text-left">
  //         {company ? (
  //           <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80">
  //             {company}
  //           </span>
  //         ) : (
  //           <span className="text-muted-foreground">N/A</span>
  //         )}
  //       </div>
  //     );
  //   },
  // },
  {
    accessorKey: "isActive",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const isActive = row.getValue("isActive") as boolean;
      return (
        <div className="text-left">
          {isActive ? (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-green-100 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-red-100 text-red-800">
              Inactive
            </span>
          )}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.getValue("isActive") as boolean;
      const statusB = rowB.getValue("isActive") as boolean;
      if (statusA && !statusB) return -1;
      if (!statusA && statusB) return 1;
      return 0;
    },
  },
];
